import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Separator } from '@/components/ui/separator'
import { ExternalLink, CreditCard, Calendar, Info, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import { useQueryClient } from '@tanstack/react-query'
import useGetSubscription from '@/services/api/account/useGetSubscription'
import SpaceSelectComponent from './SpaceSelectComponent'
import useAuthStore from '@/store/useAuthStore'
import httpClient from '@/lib/httpClient'

export default function BillingSection() {
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const [selectedTeamId, setSelectedTeamId] = useState<string>(currentTeamId)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)
  const queryClient = useQueryClient()
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    status: subscriptionLoadingStatus,
  } = useGetSubscription({ teamId: selectedTeamId })

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      const response = await httpClient.get('team/team/subscription', {
        params: { refresh_cache: true },
        headers: { 'X-Team': selectedTeamId },
      })
      // Populate React Query cache directly — avoids a redundant second request
      queryClient.setQueryData(['subscription', selectedTeamId], response.data)
    } catch {
      toast.error('Failed to refresh subscription data')
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleManageSubscription = async () => {
    if (!selectedTeamId) {
      toast.error('Please select a space first')
      return
    }

    setIsOpeningPortal(true)
    try {
      const response = await httpClient.get('team/team/subscription/create-session', {
        headers: { 'X-Team': selectedTeamId },
      })
      window.open(response.data.url, '_blank')
    } catch {
      toast.error('Failed to open billing portal')
    } finally {
      setIsOpeningPortal(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTrialDate = (dateString: string) => {
    const d = new Date(dateString)
    const day = String(d.getUTCDate()).padStart(2, '0')
    const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
    const year = d.getUTCFullYear()
    return `${day}-${month}-${year}`
  }

  const formatAmount = (amount: number, currency: string, interval: string, intervalCount: number) => {
    const price = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100)
    const period = intervalCount === 1 ? interval : `${intervalCount} ${interval}s`
    return `${price} / ${period}`
  }

  const isPersonalSpace = subscription?.team_type === 'Personal'
  const sub = subscription?.subscription

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2>Billing</h2>
          <p className="text-muted-foreground">
            Manage your subscription and billing information
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing || subscriptionLoading || !selectedTeamId}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Separator />

      <div className="space-y-4">
        <SpaceSelectComponent
          selectedTeamId={selectedTeamId}
          onValueChange={(val) => {
            setSelectedTeamId(val)
          }}
        />
        {subscriptionLoadingStatus === 'error' && (
          <div className="space-y-4">
            <Card className="w-full p-6">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-destructive" />
                <span>Failed to load subscription details...</span>
              </div>
            </Card>
          </div>
        )}

        {selectedTeamId && subscriptionLoadingStatus !== 'error' && (
          <div className="space-y-4">
            {subscriptionLoading ? (
              <Card className="w-full p-6">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                  <span>Loading subscription details...</span>
                </div>
              </Card>
            ) : isPersonalSpace ? (
              <Card className="w-full p-6">
                <div className="flex items-start space-x-3">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Personal space is included in business subscription</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Manage billing through your business space.
                    </p>
                  </div>
                </div>
              </Card>
            ) : subscription?.has_active_subscription ? (
              <Card className="w-full p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3>Current Subscription</h3>
                  <div
                    className={`rounded px-2 py-1 text-sm ${
                      sub?.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {sub?.status}
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Plan</p>
                      <p>{sub?.plan_name}</p>
                      {sub?.amount != null && sub?.currency && sub?.interval && (
                        <p className="text-sm text-muted-foreground">
                          {formatAmount(sub.amount, sub.currency, sub.interval, sub.interval_count ?? 1)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {sub?.is_trialing ? 'Trial Ends' : 'Next Billing'}
                      </p>
                      <p>
                        {sub?.is_trialing
                          ? (sub.trial_ends_at ? formatTrialDate(sub.trial_ends_at) : 'N/A')
                          : (sub?.current_period_end ? formatDate(sub.current_period_end) : 'Not set')}
                      </p>
                    </div>
                  </div>
                </div>

                {sub?.description && (
                  <p className="mb-4 text-sm text-muted-foreground">{sub.description}</p>
                )}

                <Button onClick={handleManageSubscription} disabled={isOpeningPortal} className="w-full">
                  {isOpeningPortal ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Connecting to Stripe...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Manage Subscription in Stripe
                    </>
                  )}
                </Button>
              </Card>
            ) : (
              <Card className="w-full p-6 text-center">
                <div className="mb-4">
                  <CreditCard className="mx-auto h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="mb-2">No Active Subscription</h3>
                <p className="mb-4 text-muted-foreground">
                  This space doesn't have an active subscription.
                </p>
                <Button
                  onClick={handleManageSubscription}
                  disabled={isOpeningPortal}
                >
                  {isOpeningPortal ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Connecting to Stripe...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Manage Billing in Stripe
                    </>
                  )}
                </Button>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
